import { Select } from 'antd'
import classNames from 'classnames'
import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SuffixIcon } from '../../../modules/searchResults/components/SuffixIcon'
import PerIcon from '../Icon'
import './Search.less'

export enum SearchType {
  expandable, // if type Expandable -> dropdown is visible and search input is initially closed, expanded on search-icon click
  fixed, // if type Fixed -> show only search input without dropdown, search is always in full width
}

type CommonProps = {
  value: string | undefined
  searchTerm?: string
  onSearch: (value: string) => void
  onChange?: () => void
  placeholder?: string
  className?: string
}

type ConditionalProps<T> =
  | {
      type: SearchType.expandable
      options?: T[]
      renderOptions: (option: T) => JSX.Element
      notFoundContent: ReactNode
      onOptionClick: (option: T) => void
      loading: boolean
      customDropdown: (menu: ReactNode) => JSX.Element
      onInputKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
      onIconClicked?: () => void
    }
  | { type: SearchType.fixed }

type SearchProps<T> = CommonProps & ConditionalProps<T>

export const Search = <T extends unknown>({
  value,
  searchTerm = '',
  onSearch,
  onChange,
  placeholder,
  className,
  ...rest
}: SearchProps<T>): JSX.Element => {
  const expandable = rest.type === SearchType.expandable
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  const [t] = useTranslation()

  const handleSearch = (value: string) => {
    onSearch(value.trim())
  }
  const handleChange = (value: string | undefined) => {
    if (onChange && value === undefined) {
      onChange()
    }
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClickOutside = (event: any) => {
      const clickOnWrapper = wrapperRef.current && wrapperRef.current.contains(event.target)
      const clickOnOption = typeof event.target.className === 'string' && event.target.className.includes('option')
      const clickOnScroll = typeof event.target.className === 'string' && event.target.className.includes('ant-select-dropdown-menu')
      const clickOnDelete = typeof event.target.className === 'string' && event.target.className.includes('deleteOption')

      if (clickOnWrapper && !isOpened) {
        setIsOpened(true)
      } else if (!clickOnWrapper && !clickOnOption && !clickOnScroll && !clickOnDelete && isOpened) {
        setIsOpened(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef, isOpened])

  return (
    <div ref={wrapperRef} className={classNames('wrapper', { 'wrapper-active': isOpened })} tabIndex={0} data-e2e-search>
      {expandable && !isOpened && (
        <PerIcon
          testId="search-indicator"
          name="magnifier_fill"
          size={16}
          tooltip={t('navigation.tooltip.search')}
          className="wrapper-icon"
        />
      )}
      <Select
        onInputKeyDown={expandable ? rest.onInputKeyDown : undefined}
        onSearch={handleSearch}
        onChange={handleChange}
        showSearch
        value={value}
        placeholder={placeholder}
        filterOption={false}
        allowClear
        defaultActiveFirstOption={false}
        dropdownMatchSelectWidth
        notFoundContent={expandable && rest.notFoundContent}
        dropdownRender={expandable ? rest.customDropdown : undefined}
        dropdownClassName={classNames({ 'dropdown-disabled': !isOpened })}
        suffixIcon={
          <SuffixIcon
            loading={expandable && rest.loading}
            expandable={expandable}
            searchTerm={searchTerm}
            onClick={expandable ? rest.onIconClicked : undefined}
          />
        }
        clearIcon={<PerIcon name="close_stroke" size={12} color="secondary" />}
        className={classNames('search', className, { 'search-disabled': !isOpened && expandable })}
        removeIcon={<PerIcon name="close_stroke" size={12} color="secondary" />}
        data-e2e-search-select
      >
        {expandable && rest.options && rest.options.map(rest.renderOptions)}
      </Select>
    </div>
  )
}

export function highlightSearchTerm(value?: string, term?: string): JSX.Element {
  let idx = -1
  if (term && value) {
    const _item = value.toLowerCase()
    const _term = term.toLowerCase()
    idx = _item.indexOf(_term)
    if (idx > -1) {
      return (
        <>
          {value.substring(0, idx)}
          <span className="option-highlight">{value.substring(idx, idx + term.length)}</span>
          {value.substring(idx + term.length)}
        </>
      )
    }
  }
  return <>{value}</>
}
